import { Component } from '@theme/component';
import { VariantSelectedEvent, VariantUpdateEvent } from '@theme/events';
import { morph } from '@theme/morph';
import { requestYieldCallback, getViewParameterValue } from '@theme/utilities';

/**
 * @typedef {object} VariantPickerRefs
 * @property {HTMLFieldSetElement[]} fieldsets – The fieldset elements.
 */

/**
 * A custom element that manages a variant picker.
 *
 * @template {import('@theme/component').Refs} [TRefs=VariantPickerRefs]
 * @extends Component<TRefs>
 */
export default class VariantPicker extends Component {
  /** @type {string | undefined} */
  #pendingRequestUrl;

  /** @type {AbortController | undefined} */
  #abortController;

  /** @type {number[][]} */
  #checkedIndices = [];

  /** @type {HTMLInputElement[][]} */
  #radios = [];

  /** @type {Map<string, any>} */
  #variantsByOptionValueIds = new Map();

  /** @type {Map<string, any>} */
  #variantsById = new Map();

  /** @type {HTMLElement | null | undefined} */
  #cnstrcContainer;

  /** @type {(event: Event) => void} */
  #boundHandleDocumentChange = this.#handleDocumentChange.bind(this);

  connectedCallback() {
    super.connectedCallback();
    this.#cacheVariantsFromScript();
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);
      const detectedAsSize = this.#isSizeFieldset(fieldset);

      if (detectedAsSize) {
        // A size option with a single value (e.g. a dickey with one closing size)
        // has nothing to choose between — Liquid renders it pre-selected, so keep
        // it checked instead of clearing it, otherwise product-form.js blocks
        // add-to-cart with "Select a size".
        if (radios.length === 1) {
          const [radio] = radios;
          if (radio) {
            radio.checked = true;
            radio.dataset.currentChecked = 'true';
          }
          this.#checkedIndices.push([0]);
          return;
        }

        // When a ?variant= is present on the main product page, the server renders
        // the matching size as checked. Preserve that selection instead of clearing
        // it, so a shared variant link lands with the correct size pill selected.
        if (this.#shouldPreserveInitialSize()) {
          const checkedIndex = radios.findIndex(
            (radio) => radio.dataset.currentChecked === 'true' || radio.checked,
          );
          if (checkedIndex !== -1) {
            radios.forEach((radio, index) => {
              const isChecked = index === checkedIndex;
              radio.checked = isChecked;
              radio.dataset.currentChecked = isChecked ? 'true' : 'false';
            });
            this.#checkedIndices.push([checkedIndex]);
            return;
          }
        }

        radios.forEach((radio) => {
          radio.checked = false;
          radio.dataset.currentChecked = 'false';
        });
        const swatchValue = fieldset.querySelector('legend .variant-option__swatch-value');
        if (swatchValue) swatchValue.textContent = '';
        this.#checkedIndices.push([]);
        return;
      }

      const initialCheckedIndex = radios.findIndex(
        (radio) => radio.dataset.currentChecked === 'true',
      );
      if (initialCheckedIndex !== -1) {
        this.#checkedIndices.push([initialCheckedIndex]);
      }
    });

    this.addEventListener('change', this.variantChanged.bind(this));
    document.addEventListener('change', this.#boundHandleDocumentChange);
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener('change', this.#boundHandleDocumentChange);
    this.#abortController?.abort();
  }

  /**
   * Returns true when the fieldset represents a Size option (any locale).
   * @param {HTMLFieldSetElement} fieldset
   * @returns {boolean}
   */
  #isSizeFieldset(fieldset) {
    const value = fieldset.dataset.fieldsetValue;
    const optionPosition = fieldset.dataset.fieldsetOptionPosition;
    return optionPosition === '2' || value === 'Size' || value === 'Taille';
  }

  /**
   * Returns true when this is the main PDP picker and the URL carries a variant,
   * meaning the server-rendered size selection should be kept on initial load.
   * @returns {boolean}
   */
  #shouldPreserveInitialSize() {
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !this.closest('product-card') &&
      !this.closest('quick-add-dialog');
    if (!isOnProductPage) return false;

    try {
      return new URL(window.location.href).searchParams.has('variant');
    } catch {
      return false;
    }
  }

  /** Returns true if the picker currently has a selected size. @returns {boolean} */
  #hasSelectedSize() {
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    const sizeFieldset = fieldsets.find((fieldset) => this.#isSizeFieldset(fieldset));
    if (!sizeFieldset) return false;

    return Boolean(sizeFieldset.querySelector('input[type="radio"]:checked'));
  }

  /**
   * Handles document-level change events for product-grouping and extended-product inputs.
   * @param {Event} event - The change event.
   */
  #handleDocumentChange(event) {
    if (!(event.target instanceof HTMLInputElement)) return;
    const isProductGrouping = event.target.name?.startsWith('product-grouping-');
    const isExtendedProduct = event.target.name?.startsWith('extended-product-');

    if ((isProductGrouping || isExtendedProduct) && this.dataset.templateProductMatch === 'true') {
      const productId = this.dataset.productId;
      if (productId && event.target.name.includes(productId)) {
        this.variantChanged(event);
      }
    }
  }

  /**
   * Handles the variant change event.
   * @param {Event} event - The variant change event.
   */
  variantChanged(event) {
    if (!(event.target instanceof HTMLElement)) return;

    const selectedOption =
      event.target instanceof HTMLSelectElement
        ? event.target.options[event.target.selectedIndex]
        : event.target;

    if (!selectedOption) return;

    // Hide "Please select a size" error once a size is chosen
    if (event.target.closest('[data-fieldset-option-position="2"]')) {
      const section = this.closest(
        '.mini-pdp__product-content, .shopify-section, dialog, product-card',
      );
      section?.querySelector('.size-select-error')?.classList.add('hidden');
    }

    const newProductUrl = selectedOption.dataset.connectedProductUrl;
    const newProductHandle = selectedOption.dataset.productHandle;

    this.updateSelectedOption(event.target);

    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !event.target.closest('product-card') &&
      !event.target.closest('quick-add-dialog');

    // Morph the entire main content for combined listings child products or product-grouping swatches,
    // because changing the product might also change other sections depending on recommendations, metafields, etc.
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const pendingUrl = this.#pendingRequestUrl?.split('?')[0];
    const swapInFlight = !!pendingUrl && pendingUrl !== currentUrl;
    const loadsNewProduct =
      isOnProductPage && ((!!newProductUrl && newProductUrl !== currentUrl) || swapInFlight);
    const isOnFeaturedProductSection = Boolean(this.closest('featured-product-information'));
    const localVariant =
      isOnProductPage && !loadsNewProduct
        ? this.#resolveVariantFromSelection(selectedOption)
        : undefined;

    const selectedVariantId =
      localVariant?.id ??
      (selectedOption instanceof HTMLElement ? (selectedOption.dataset.variantId ?? '') : '');
    this.#updateCnstrcVariationId(selectedVariantId);

    const variantSelectionResource =
      /** @type {{ id: string, loadsNewProduct?: boolean, hasLocalVariant?: boolean }} */ ({
        id: selectedOption.dataset.optionValueId ?? '',
        loadsNewProduct,
        hasLocalVariant: Boolean(localVariant),
      });

    this.dispatchEvent(new VariantSelectedEvent(variantSelectionResource));

    // On same-product PDP selections, dispatch a local update immediately so
    // button state and form data can update without waiting for server HTML.
    if (isOnProductPage && !loadsNewProduct && localVariant) {
      const localSourceId =
        selectedOption instanceof HTMLElement ? (selectedOption.dataset.optionValueId ?? '') : '';
      this.dispatchEvent(
        new VariantUpdateEvent(localVariant, localSourceId, {
          html: document,
          productId: this.dataset.productId ?? '',
          newProduct: undefined,
        }),
      );
    }

    if (isOnProductPage && !loadsNewProduct) {
      this.#updateLowStockMessage();
    }

    const morphElementSelector = loadsNewProduct
      ? 'main'
      : isOnFeaturedProductSection
        ? 'featured-product-information'
        : undefined;

    const requestUrl = this.buildRequestUrl(selectedOption);
    this.fetchUpdatedSection(requestUrl, morphElementSelector);

    // Build the new URL
    const url = new URL(window.location.href);

    // Change the path if the option is connected to another product via combined listing or product-grouping.
    if (loadsNewProduct) {
      // Use the product handle to build the new pathname
      if (newProductHandle) {
        const routeRoot = window.Shopify?.routes?.root || '/';
        url.pathname = `${routeRoot}products/${newProductHandle}`;
      } else if (newProductUrl) {
        url.pathname = newProductUrl;
      }
    }

    if (isOnProductPage) {
      if (loadsNewProduct) {
        url.searchParams.delete('variant');
      } else {
        const variantId =
          selectedOption instanceof HTMLElement ? selectedOption.dataset.variantId : undefined;
        if (variantId) {
          url.searchParams.set('variant', variantId);
        } else {
          url.searchParams.delete('variant');
        }
      }
    }

    if (url.href !== window.location.href) {
      requestYieldCallback(() => {
        history.replaceState({}, '', url.toString());
      });
    }
  }

  /**
   * Updates the selected option.
   * @param {string | Element} target
   */
  updateSelectedOption(target) {
    if (typeof target === 'string') {
      const targetElement = this.querySelector(`[data-option-value-id="${target}"]`);

      if (!targetElement) throw new Error('Target element not found');

      target = targetElement;
    }

    if (target instanceof HTMLInputElement) {
      const fieldsetIndex = Number.parseInt(target.dataset.fieldsetIndex || '');
      const inputIndex = Number.parseInt(target.dataset.inputIndex || '');

      if (!Number.isNaN(fieldsetIndex) && !Number.isNaN(inputIndex)) {
        const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
        const fieldset = fieldsets[fieldsetIndex];
        const checkedIndices = this.#checkedIndices[fieldsetIndex];
        const radios = this.#radios[fieldsetIndex];

        if (radios && checkedIndices && fieldset) {
          // Clear previous checked states
          const [currentIndex, previousIndex] = checkedIndices;

          if (currentIndex !== undefined && radios[currentIndex]) {
            radios[currentIndex].dataset.previousChecked = 'false';
          }
          if (previousIndex !== undefined && radios[previousIndex]) {
            radios[previousIndex].dataset.previousChecked = 'false';
          }

          // Update checked indices array - keep only the last 2 selections
          checkedIndices.unshift(inputIndex);
          checkedIndices.length = Math.min(checkedIndices.length, 2);

          // Update the new states
          const newCurrentIndex = checkedIndices[0]; // This is always inputIndex
          const newPreviousIndex = checkedIndices[1]; // This might be undefined

          // newCurrentIndex is guaranteed to exist since we just added it
          if (newCurrentIndex !== undefined && radios[newCurrentIndex]) {
            radios[newCurrentIndex].dataset.currentChecked = 'true';
            fieldset.style.setProperty(
              '--pill-width-current',
              `${radios[newCurrentIndex].parentElement?.offsetWidth || 0}px`,
            );
          }

          if (newPreviousIndex !== undefined && radios[newPreviousIndex]) {
            radios[newPreviousIndex].dataset.previousChecked = 'true';
            radios[newPreviousIndex].dataset.currentChecked = 'false';
            fieldset.style.setProperty(
              '--pill-width-previous',
              `${radios[newPreviousIndex].parentElement?.offsetWidth || 0}px`,
            );
          }

          // Keep legend value in sync immediately for perceived responsiveness.
          const swatchValue = fieldset.querySelector('legend .variant-option__swatch-value');
          if (swatchValue) {
            swatchValue.textContent = `— ${target.value}`;
          }
        }
      }
      target.checked = true;
    }

    if (target instanceof HTMLSelectElement) {
      const newValue = target.value;
      const newSelectedOption = Array.from(target.options).find(
        (option) => option.value === newValue,
      );

      if (!newSelectedOption) throw new Error('Option not found');

      for (const option of target.options) {
        option.removeAttribute('selected');
      }

      newSelectedOption.setAttribute('selected', 'selected');

      // Keep legend value in sync immediately for perceived responsiveness.
      const swatchValue = target
        .closest('.variant-option')
        ?.querySelector('legend .variant-option__swatch-value');
      if (swatchValue) {
        swatchValue.textContent = `— ${newSelectedOption.textContent?.trim() || newValue}`;
      }
    }
  }

  /**
   * Builds the request URL.
   * @param {HTMLElement} selectedOption
   * @param {string | null} [source]
   * @param {string[]} [sourceSelectedOptionsValues]
   * @returns {string} The request URL.
   */
  buildRequestUrl(selectedOption, source = null, sourceSelectedOptionsValues = []) {
    // this productUrl and pendingRequestUrl will be useful for the support of combined listing. It is used when a user changes variant quickly and those products are using separate URLs (combined listing).
    // We create a new URL and abort the previous fetch request if it's still pending.
    let productUrl =
      selectedOption.dataset.connectedProductUrl ||
      this.#pendingRequestUrl ||
      this.dataset.productUrl;
    this.#pendingRequestUrl = productUrl;
    const params = [];
    const viewParamValue = getViewParameterValue();

    // preserve view parameter, if it exists, for alternative product view testing
    if (viewParamValue) params.push(`view=${viewParamValue}`);

    // Check if this is a product-grouping swatch (switching to a different product)
    const isProductGroupingSwatch =
      selectedOption instanceof HTMLInputElement &&
      selectedOption.name?.startsWith('product-grouping-');
    const isExtendedProduct =
      selectedOption instanceof HTMLInputElement &&
      selectedOption.name?.startsWith('extended-product-');
    const isOnProductPage =
      this.dataset.templateProductMatch === 'true' &&
      !selectedOption.closest('product-card') &&
      !selectedOption.closest('quick-add-dialog');
    const currentUrl = this.dataset.productUrl?.split('?')[0];
    const loadsNewProduct = isOnProductPage && !!productUrl && productUrl !== currentUrl;

    // For product-grouping swatches that switch products, we need to fetch the full page
    // For regular variant changes, use option_values
    if ((isProductGroupingSwatch || isExtendedProduct) && loadsNewProduct) {
      // Only carry a size-specific variant when a size is actively selected.
      // Without a selected size, forcing variant can surface out-of-stock CTA text
      // on colorway switches even though no variant should be selected by default.
      let variantId;
      if (isProductGroupingSwatch) {
        variantId = this.#hasSelectedSize()
          ? selectedOption.dataset.sizeVariantId || selectedOption.dataset.variantId
          : undefined;
      } else {
        variantId = selectedOption.dataset.variantId;
      }

      if (variantId) {
        params.push(`variant=${variantId}`);
      }
      // Return the full product URL - when morphElementSelector is 'main', fetchUpdatedSection will fetch the full page
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?${params.join('&')}`;
    }

    if (this.selectedOptionsValues.length && !source) {
      params.push(`option_values=${this.selectedOptionsValues.join(',')}`);
    } else if (source === 'product-card') {
      if (this.selectedOptionsValues.length) {
        params.push(`option_values=${sourceSelectedOptionsValues.join(',')}`);
      } else {
        params.push(`option_values=${selectedOption.dataset.optionValueId}`);
      }
    }

    // If variant-picker is a child of some specific sections, we need to append section_id=xxxx to the URL
    const SECTION_ID_MAP = {
      'quick-add-component': 'section-rendering-product-card',
      'swatches-variant-picker-component': 'section-rendering-product-card',
      'featured-product-information': this.closest('featured-product-information')?.id,
    };

    const closestSectionId = /** @type {keyof typeof SECTION_ID_MAP} | undefined */ (
      Object.keys(SECTION_ID_MAP).find((sectionId) => this.closest(sectionId))
    );

    if (closestSectionId) {
      if (productUrl?.includes('?')) {
        productUrl = productUrl.split('?')[0];
      }
      return `${productUrl}?section_id=${SECTION_ID_MAP[closestSectionId]}&${params.join('&')}`;
    }

    return `${productUrl}?${params.join('&')}`;
  }

  /**
   * Fetches the updated section.
   * @param {string} requestUrl
   * @param {string} [morphElementSelector] - The selector of the element to be morphed. By default, only the variant picker is morphed.
   */
  async fetchUpdatedSection(requestUrl, morphElementSelector) {
    // We use this to abort the previous fetch request if it's still pending.
    this.#abortController?.abort();
    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    try {
      const response = await fetch(requestUrl, { signal });
      const responseText = await response.text();

      this.#pendingRequestUrl = undefined;
      const html = new DOMParser().parseFromString(responseText, 'text/html');
      // Defer is only useful for the initial rendering of the page. Remove it here.
      html.querySelector('overflow-list[defer]')?.removeAttribute('defer');

      if (morphElementSelector === 'main') {
        this.updateMain(html);
        return;
      }

      const textContent = html.querySelector(
        `variant-picker script[type="application/json"]`,
      )?.textContent;
      if (!textContent) return;

      if (morphElementSelector) {
        this.updateElement(html, morphElementSelector);
      } else {
        const newProduct = this.updateVariantPicker(html);

        // We grab the variant object from the response and dispatch an event with it.
        if (this.selectedOptionId) {
          this.dispatchEvent(
            new VariantUpdateEvent(JSON.parse(textContent), this.selectedOptionId, {
              html,
              productId: this.dataset.productId ?? '',
              newProduct,
            }),
          );
        }
      }
    } catch (error) {
      if (error.name !== 'AbortError') {
        console.error(error);
      }
    }
  }

  /**
   * @typedef {Object} NewProduct
   * @property {string} id
   * @property {string} url
   */

  /**
   * Re-renders the variant picker.
   * @param {Document} newHtml
   * @returns {NewProduct | undefined} Information about the new product if it has changed, otherwise undefined.
   */
  updateVariantPicker(newHtml) {
    /** @type {NewProduct | undefined} */
    let newProduct;

    const newVariantPickerSource = newHtml.querySelector(this.tagName.toLowerCase());

    if (!newVariantPickerSource) {
      throw new Error('No new variant picker source found');
    }

    // For combined listings, the product might have changed, so update the related data attribute.
    if (newVariantPickerSource instanceof HTMLElement) {
      const newProductId = newVariantPickerSource.dataset.productId;
      const newProductUrl = newVariantPickerSource.dataset.productUrl;

      if (newProductId && newProductUrl && this.dataset.productId !== newProductId) {
        newProduct = { id: newProductId, url: newProductUrl };
      }

      this.dataset.productId = newProductId;
      this.dataset.productUrl = newProductUrl;
    }

    morph(this, newVariantPickerSource);
    this.#cacheVariantsFromScript();

    return newProduct;
  }

  /**
   * Re-renders the desired element.
   * @param {Document} newHtml
   * @param {string} elementSelector - The selector of the element to re-render.
   */
  updateElement(newHtml, elementSelector) {
    const element = this.closest(elementSelector);
    const newElement = newHtml.querySelector(elementSelector);

    if (!element || !newElement) {
      throw new Error(`No new element source found for ${elementSelector}`);
    }

    morph(element, newElement);
  }

  /**
   * Re-renders the entire main content.
   * @param {Document} newHtml
   */
  updateMain(newHtml) {
    const main = document.querySelector('main');
    const newMain = newHtml.querySelector('main');

    if (!main || !newMain) {
      throw new Error('No new main source found');
    }

    // Capture the currently selected size value before morphing so we can
    // re-select the same size on the new product (if it exists).
    let selectedSizeValue = null;
    const currentFieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    for (const fieldset of currentFieldsets) {
      if (this.#isSizeFieldset(fieldset)) {
        const checkedRadio = fieldset.querySelector('input[type="radio"]:checked');
        if (checkedRadio instanceof HTMLInputElement) {
          selectedSizeValue = checkedRadio.value;
        }
        break;
      }
    }

    let newProductId;
    let newProductUrl;

    // Update this variant-picker's data attributes from the new variant-picker in the response
    const newVariantPicker = newHtml.querySelector('variant-picker');
    if (newVariantPicker instanceof HTMLElement) {
      newProductId = newVariantPicker.dataset.productId;
      newProductUrl = newVariantPicker.dataset.productUrl;

      if (newProductId) {
        this.dataset.productId = newProductId;
      }
      if (newProductUrl) {
        this.dataset.productUrl = newProductUrl;
      }
    }

    // In the single-row swatch variant of the mobile PDP test, `.product-grouping` is a horizontal
    // scroller, and the morph below swaps that node out for a fresh one rather than updating it in
    // place — so the row snaps back to the first swatch on every colourway switch. The grouping is
    // identical across the products in it, so carrying the offset over lands the shopper exactly
    // where they were. No-op everywhere else, where the row doesn't scroll.
    const groupingScrollOffsets = Array.from(main.querySelectorAll('.product-grouping')).map(
      (grouping) => grouping.scrollLeft,
    );

    morph(main, newMain);

    // Reapplied synchronously: the swatches are laid out by the time the morph returns, so the new
    // node already scrolls to the same extent as the one it replaced.
    const morphedGroupings = main.querySelectorAll('.product-grouping');
    groupingScrollOffsets.forEach((scrollLeft, index) => {
      const grouping = morphedGroupings[index];
      if (grouping && scrollLeft) grouping.scrollLeft = scrollLeft;
    });

    this.#cacheVariantsFromScript();

    // connectedCallback won't re-run on morph, so manually handle size state.
    // Re-initialise #radios and #checkedIndices from the morphed fieldsets so
    // subsequent variantChanged calls reference current DOM nodes.
    const fieldsets = /** @type {HTMLFieldSetElement[]} */ (this.refs.fieldsets || []);
    this.#radios = [];
    this.#checkedIndices = [];

    fieldsets.forEach((fieldset) => {
      const radios = Array.from(fieldset?.querySelectorAll('input') ?? []);
      this.#radios.push(radios);

      if (this.#isSizeFieldset(fieldset)) {
        // Try to find and re-select the previously selected size on the new product.
        // A lone size is always the selection — the shopper has no other option —
        // so fall back to it when the previous size doesn't exist on the new product.
        const loneRadio = radios.length === 1 ? radios[0] : null;
        const matchingRadio =
          (selectedSizeValue ? radios.find((radio) => radio.value === selectedSizeValue) : null) ??
          loneRadio;

        // Clear all size radios first (common to both branches).
        radios.forEach((radio) => {
          radio.checked = false;
          radio.dataset.currentChecked = 'false';
        });

        const swatchValue = fieldset.querySelector('legend .variant-option__swatch-value');

        if (matchingRadio) {
          // Size exists on the new product — select it.
          matchingRadio.checked = true;
          matchingRadio.dataset.currentChecked = 'true';
          // Read the value off the radio itself: it may be the lone-size fallback
          // rather than the size that was selected before the morph.
          if (swatchValue) swatchValue.textContent = matchingRadio.value;

          const inputIndex = Number.parseInt(matchingRadio.dataset.inputIndex || '0', 10);
          this.#checkedIndices.push([inputIndex]);

          // Defer the change dispatch so updateMain completes before
          // variantChanged triggers a second fetchUpdatedSection call.
          queueMicrotask(() => {
            matchingRadio.dispatchEvent(new Event('change', { bubbles: true }));
          });
        } else {
          // Size does not exist on the new product — clear selection.
          if (swatchValue) swatchValue.textContent = '';
          this.#checkedIndices.push([]);
        }
      } else {
        // Non-size fieldsets: preserve the checked state from the morphed HTML.
        const checkedIndex = radios.findIndex((radio) => radio.dataset.currentChecked === 'true');
        if (checkedIndex !== -1) {
          this.#checkedIndices.push([checkedIndex]);
        } else {
          this.#checkedIndices.push([]);
        }
      }
    });

    // Scripts inside morphed HTML don't re-execute, so dispatch variant:update here
    // so that document-level listeners (e.g. Stylitics) can react to the new product.
    if (newProductId) {
      const variantUpdateResource =
        /** @type {import('@theme/events').VariantUpdateEvent['detail']['resource']} */ (
          /** @type {unknown} */ (null)
        );
      document.dispatchEvent(
        new VariantUpdateEvent(variantUpdateResource, '', {
          html: newHtml,
          productId: newProductId,
          newProduct: { id: newProductId, url: newProductUrl ?? '' },
        }),
      );
    }

    // Re-initialize Yotpo reviews for the new product. The morph resets the widget
    // container to the empty server-rendered placeholder, so Yotpo must be refreshed.
    const yotpoWidgetsContainer = /** @type {{ initWidgets?: () => void } | undefined} */ (
      /** @type {any} */ (window).yotpoWidgetsContainer
    );
    yotpoWidgetsContainer?.initWidgets?.();
  }

  /** Caches variant lookup maps from embedded JSON payload. */
  #cacheVariantsFromScript() {
    this.#variantsByOptionValueIds.clear();
    this.#variantsById.clear();

    const script = this.querySelector('script[data-variant-list][type="application/json"]');
    if (!(script instanceof HTMLScriptElement) || !script.textContent) return;

    try {
      const variants = JSON.parse(script.textContent);
      if (!Array.isArray(variants)) return;

      for (const variant of variants) {
        if (!variant || typeof variant !== 'object') continue;

        const variantId = variant.id == null ? '' : String(variant.id);
        if (variantId) {
          this.#variantsById.set(variantId, variant);
        }

        if (Array.isArray(variant.option_value_ids) && variant.option_value_ids.length) {
          const key = variant.option_value_ids
            .map((/** @type {string | number} */ id) => String(id))
            .join(',');
          this.#variantsByOptionValueIds.set(key, variant);
        }
      }
    } catch (error) {
      console.error(error);
    }
  }

  /**
   * Resolves the current variant using frontloaded data for same-product updates.
   * @param {HTMLElement} selectedOption
   * @returns {any | undefined}
   */
  #resolveVariantFromSelection(selectedOption) {
    const selectedOptionIds = this.selectedOptionsValues;
    if (selectedOptionIds.length) {
      const key = selectedOptionIds.map((id) => String(id)).join(',');
      const matchedVariant = this.#variantsByOptionValueIds.get(key);
      if (matchedVariant) return matchedVariant;
    }

    const variantId = selectedOption.dataset.variantId;
    if (variantId) {
      return this.#variantsById.get(String(variantId));
    }

    return undefined;
  }

  /** Updates the low-stock badge in the size fieldset legend client-side. */
  #updateLowStockMessage() {
    const container = this.querySelector(
      '[data-fieldset-option-position="2"] .variant-option__low-stock',
    );
    if (!container) return;

    const key = this.selectedOptionsValues.map(String).join(',');
    const variant = key ? this.#variantsByOptionValueIds.get(key) : undefined;
    const threshold = Number.parseInt(this.dataset.lowStockThreshold ?? '', 10);

    const shouldShow =
      variant != null &&
      variant.available === true &&
      Number.isFinite(threshold) &&
      typeof variant.inventory_quantity === 'number' &&
      variant.inventory_quantity > 0 &&
      variant.inventory_quantity <= threshold;

    let message = container.querySelector('.low-stock-message');

    if (!shouldShow) {
      message?.remove();
      return;
    }

    if (!message) {
      message = document.createElement('span');
      message.className = 'low-stock-message low-stock-message--pdp';
      message.setAttribute('role', 'status');
      message.setAttribute('aria-live', 'polite');
      container.appendChild(message);
    }

    message.setAttribute('data-variant-id', String(variant.id));
    message.textContent = this.dataset.lowStockMessage ?? '';
  }

  /**
   * Keep Constructor variation tracking attribute in sync with selected variant.
   * @param {string | number | undefined} variantId
   */
  #updateCnstrcVariationId(variantId) {
    if (this.#cnstrcContainer === undefined) {
      const container = this.closest('[data-cnstrc-product-detail]');
      this.#cnstrcContainer = container instanceof HTMLElement ? container : null;
    }

    if (!this.#cnstrcContainer) return;

    this.#cnstrcContainer.dataset.cnstrcItemVariationId = variantId ? String(variantId) : '';
  }

  /**
   * Gets the selected option.
   * @returns {HTMLInputElement | HTMLOptionElement | undefined} The selected option.
   */
  get selectedOption() {
    const selectedOption = this.querySelector('select option[selected], fieldset input:checked');

    if (
      !(selectedOption instanceof HTMLInputElement || selectedOption instanceof HTMLOptionElement)
    ) {
      return undefined;
    }

    return selectedOption;
  }

  /**
   * Gets the selected option ID.
   * @returns {string | undefined} The selected option ID.
   */
  get selectedOptionId() {
    const { selectedOption } = this;
    if (!selectedOption) return undefined;
    const { optionValueId } = selectedOption.dataset;

    if (!optionValueId) {
      throw new Error('No option value ID found');
    }

    return optionValueId;
  }

  /**
   * Gets the selected options values.
   * @returns {string[]} The selected options values.
   */
  get selectedOptionsValues() {
    /** @type HTMLElement[] */
    const selectedOptions = Array.from(
      this.querySelectorAll('select option[selected], fieldset input:checked'),
    );

    return selectedOptions.map((option) => {
      const { optionValueId } = option.dataset;

      if (!optionValueId) throw new Error('No option value ID found');

      return optionValueId;
    });
  }
}

if (!customElements.get('variant-picker')) {
  customElements.define('variant-picker', VariantPicker);
}
